'use client'

import { useState } from 'react'
import { Copy, Check } from 'lucide-react'

/**
 * Small "Copy ID" pill — copies the value to clipboard with a brief
 * green-check "Copied" confirmation. Used in the help-form order picker
 * and the user's orders tab so a 36-char UUID never has to be highlighted
 * by hand on mobile.
 *
 * Falls back to a hidden-textarea + execCommand for older Safari that
 * doesn't expose `navigator.clipboard`.
 */
export const CopyIdButton = ({
  value,
  label = 'Copy ID',
  copiedLabel = 'Copied',
  size = 'sm',
}: {
  value: string
  label?: string
  copiedLabel?: string
  size?: 'sm' | 'md'
}) => {
  const [copied, setCopied] = useState(false)

  const copy = async (e: React.MouseEvent) => {
    // Stop bubbling so a parent click handler (e.g. card expand) doesn't fire.
    e.stopPropagation()
    e.preventDefault()
    try {
      await navigator.clipboard.writeText(value)
      flash()
    } catch {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        flash()
      } catch {
        /* swallow */
      }
      document.body.removeChild(ta)
    }
  }

  const flash = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const dims = size === 'md'
    ? { height: 30, font: 13, icon: 14, padCls: 'px-2 sm:px-3' }
    : { height: 26, font: 12, icon: 12, padCls: 'px-1.5 sm:px-2.5' }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? copiedLabel : label}
      title={copied ? copiedLabel : label}
      // Mobile shows icon-only (no gap, tighter padding) to save horizontal
      // space; sm+ unhides the label and adds gap. Aspect square on mobile
      // keeps the pill round when the text is hidden. The invisible 44px
      // square centred on the pill is the tap target, so the small pill still
      // meets the 44-point minimum without growing.
      className={`relative inline-flex items-center justify-center aspect-square sm:aspect-auto before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${dims.padCls}`}
      style={{
        background: 'transparent',
        border: '1px solid var(--separator)',
        borderRadius: 999,
        height: dims.height,
        fontSize: dims.font,
        color: copied ? 'var(--ios-green)' : 'var(--font_2)',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'color 120ms',
      }}
    >
      {copied ? (
        <Check size={dims.icon} strokeWidth={2.5} />
      ) : (
        <Copy size={dims.icon} />
      )}
      <span className="hidden sm:inline" style={{ marginLeft: 6 }}>
        {copied ? copiedLabel : label}
      </span>
    </button>
  )
}
